import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import env from '@/env';
import {
  Attribute,
  Cutoverplan,
  Environment,
  Module,
  Phase,
  Responsible,
  Role,
  User
} from '@/types';
import { Step } from '@/interfaces/Step';
import { Wave } from '@/interfaces/Wave';

Vue.use(Vuex);
type stateType = {
  fullBlueprints: Module[];
  modules: Module[];
  all_attributes: Attribute[];
  all_responsibles: Responsible[];
  all_roles: Role[];
  all_waves: any[];
  all_phases: Phase[];
  all_cutoverplans: Cutoverplan[];
  module_ids_to_name_map: Record<number, string>;
  all_types: any[];
  all_environments: Environment[];
  user: User;
  all_steps: Step[];
  all_blueprints: Module[];
  languages: any[];
};

const stateObject: stateType = {
  fullBlueprints: [],
  modules: [],
  all_attributes: [],
  all_responsibles: [],
  all_roles: [],
  all_waves: [],
  all_phases: [],
  all_cutoverplans: [],
  module_ids_to_name_map: {},
  all_types: [],
  all_environments: [],
  all_steps: [],
  all_blueprints: [],
  languages: [],
  user: {
    languageId: 1,
    name: '',
    permissions: {
      cutovertool: {
        access: false,
        admin: false
      },
      kanban: {
        access: false,
        editor: false,
        admin: false
      },
      admin: false
    }
  }
};

export default new Vuex.Store({
  state: stateObject,
  mutations: {
    setModules(state, modules) {
      state.modules = modules;
    },
    addModule(state, module) {
      state.modules.push(module);
    },
    addAttribute(state, attribute) {
      state.all_attributes.push(attribute);
    },
    addModuleToModuleIdsToNameMap(state, { id, name }) {
      state.module_ids_to_name_map[id] = name;
    },
    addCutoverplanToAllCutoverplans(state, cutoverplan) {
      state.all_cutoverplans.push(cutoverplan);
    },
    setPhases(state, phases: any) {
      state.all_phases = phases;
    },
    setUser(state, user: User) {
      state.user = user;
      if (!user.permissions.admin && !user.permissions.cutovertool?.access) {
        window.location.href = '/board';
      }
    }
  },
  getters: {
    restrictedUser: (state) =>
      !state.user.permissions.cutovertool?.admin && !state.user.permissions.admin,
    blankStep: () =>
      (({
        id: null,
        is_blueprint: 0,
        blueprint_id: null,
        module_id: null,
        parent_step_id: null,
        position: null,
        name: null,
        comment: null,
        duration_constant: null,
        planned_start: null,
        planned_startdate: null,
        planned_end: null,
        status: null,
        real_start: null,
        real_end: null,
        responsible_id: null,
        phase_id: null,
        separate_block: null,
        type_id: null,
        group_id: null,
        print_only_once: null,
        infoto: null,
        supportresponsible_id: null,
        responsibleTxt: null,
        todo: null,
        role_id: null,
        infoto_id: null,
        infotorole_id: null,
        appointment: null,
        hide_phase_row: null,
        hide_module_row: null,
        hidden: null,
        supportrole_id: null,
        is_change: null,
        plannedEnddate: null
      } as unknown) as Step)
  },
  actions: {
    refreshAllAttributes({ state }) {
      return axios.get(`${env.backend_url}/all_attributes`).then(({ data: attributes }) => {
        state.all_attributes = attributes;
      });
    },
    refreshAllResponsibles({ state }) {
      return axios.get(`${env.backend_url}/all_responsibles`).then(({ data: responsibles }) => {
        state.all_responsibles = responsibles;
      });
    },
    refreshAllRoles({ state }) {
      return axios.get(`${env.backend_url}/all_roles`).then(({ data: roles }) => {
        state.all_roles = roles;
      });
    },
    refreshAllCutoverplans({ state }) {
      return axios.get(`${env.backend_url}/all_cutoverplans`).then(({ data: cutoverplans }) => {
        state.all_cutoverplans = cutoverplans;
      });
    },
    refreshAllWaves({ state }) {
      return axios.get(`${env.backend_url}/all_waves`).then(({ data: waves }) => {
        state.all_waves = waves.sort((a: Wave, b: Wave) => a.sequence - b.sequence);
      });
    },
    refreshAllPhases({ state }) {
      return axios.get(`${env.backend_url}/phase/all`).then(({ data: phases }) => {
        state.all_phases = phases;
      });
    },
    refreshAllTypes({ state }) {
      return axios.get(`${env.backend_url}/all_types_steps`).then(({ data: types }) => {
        state.all_types = types;
      });
    },
    refreshAllEnvironments({ state }) {
      return axios.get(`${env.backend_url}/all_environments`).then(({ data: environments }) => {
        state.all_environments = environments;
      });
    },
    async refreshData({ state }) {
      const getAttributes = axios
        .get(`${env.backend_url}/all_attributes`)
        .then(({ data: attributes }) => {
          state.all_attributes = attributes;
        });
      const getResponsibles = axios
        .get(`${env.backend_url}/all_responsibles`)
        .then(({ data: responsibles }) => {
          state.all_responsibles = responsibles;
        });
      const getRoles = axios.get(`${env.backend_url}/all_roles`).then(({ data: roles }) => {
        state.all_roles = roles;
      });
      const getWaves = axios.get(`${env.backend_url}/all_waves`).then(({ data: waves }) => {
        state.all_waves = waves;
      });
      const getPhases = axios.get(`${env.backend_url}/phase/all`).then(({ data: phases }) => {
        state.all_phases = phases;
      });
      const getTypes = axios.get(`${env.backend_url}/all_types_steps`).then(({ data: types }) => {
        state.all_types = types;
      });

      const getLanguages = axios.get(`${env.backend_url}/language`).then(({ data: languages }) => {
        state.languages = languages;
      });

      axios
        .get(`${env.backend_url}/module`, {
          params: {
            blueprints_only: true
          }
        })
        .then(({ data: fullBlueprints }) => {
          state.fullBlueprints = fullBlueprints;
        });

      axios.get(`${env.backend_url}/module/blueprints/all`).then(({ data: blueprints }) => {
        state.all_blueprints = blueprints;

        state.all_steps = blueprints.map((blueprint: Module) => ({
          module: blueprint,
          steps: blueprint.steps
        }));
      });

      const getCutoverplans = axios
        .get(`${env.backend_url}/all_cutoverplans`)
        .then(({ data: cutoverplans }) => {
          state.all_cutoverplans = cutoverplans;
        });
      const getEnvironments = axios
        .get(`${env.backend_url}/all_environments`)
        .then(({ data: environments }) => {
          state.all_environments = environments;
        });
      return Promise.all([
        getAttributes,
        getResponsibles,
        getRoles,
        getWaves,
        getPhases,
        getTypes,
        getCutoverplans,
        getEnvironments,
        getLanguages
      ]);
    },
    async refreshIdstoNameMap({ state }) {
      await axios
        .get(`${env.backend_url}/module/ids_to_name_map`)
        .then(({ data: idsToNameMap }) => {
          state.module_ids_to_name_map = idsToNameMap;
        });
    }
  }
});
